import React, { useContext, useEffect, useState } from 'react';
import { ResponsiveLine } from '@nivo/line';
import { useChartTheme } from '../themes/chartTheme';
import {
  Box,
  useTheme,
  ToggleButtonGroup,
  ToggleButton,
  Typography,
  FormGroup,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { tokens } from '../theme';
import { FilterContext, IFilterState } from '../contexts/FilterContext';
import fetchData from '../helpers/fetchData';
import dayjs from 'dayjs';

interface IDoiRateOverTime {
  date: string;
  doi_rate: number;
  total_distinct_emails: number;
  confirmed_distinct_emails: number;
}

interface IDoiRateByEmailDomainOverTime extends IDoiRateOverTime {
  email_domain: string;
}

interface ISerie {
  id: string;
  data: { x: string; y: number; originalValue?: number }[];
}

interface DoiRateOverTimeChartProps {
  originHost: string;
  emailDomain?: string;
}

type AggregationType = 'daily' | 'weekly' | 'monthly';

// Determine the appropriate aggregation based on date range
const determineAggregation = (startDate: Date, endDate: Date): AggregationType => {
  const diffInDays = dayjs(endDate).diff(dayjs(startDate), 'day');

  if (diffInDays > 120) {
    return 'monthly';
  } else if (diffInDays > 45) {
    return 'weekly';
  } else {
    return 'daily';
  }
};

// Format the date for display based on aggregation type
const formatDisplayDate = (dateString: string, aggregation: AggregationType): string => {
  switch (aggregation) {
    case 'monthly':
      // For YYYY-MM format
      const [year, month] = dateString.split('-');
      const monthNames = [
        'Jan',
        'Feb',
        'Mar',
        'Apr',
        'May',
        'Jun',
        'Jul',
        'Aug',
        'Sep',
        'Oct',
        'Nov',
        'Dec',
      ];
      return `${monthNames[parseInt(month) - 1]} ${year}`;

    case 'weekly':
      // For YYYY-WW format
      const [weekYear, weekNum] = dateString.split('-');
      return `W${weekNum} ${weekYear}`;

    case 'daily':
    default:
      // Keep YYYY-MM-DD format but make it more readable
      if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
        return dayjs(dateString).format('DD.MM.YYYY');
      }
      return dateString;
  }
};

const DoiRateOverTimeChart: React.FC<DoiRateOverTimeChartProps> = ({ originHost, emailDomain }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const chartTheme = useChartTheme();
  const { filterState } = useContext(FilterContext)!;
  const [chartData, setChartData] = useState<ISerie[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [suggestedAggregation, setSuggestedAggregation] = useState<AggregationType>('daily');
  const [selectedAggregation, setSelectedAggregation] = useState<AggregationType | null>(null);

  // Series visibility state - all visible by default
  const [showDoiRate, setShowDoiRate] = useState<boolean>(true);
  const [showSoiCount, setShowSoiCount] = useState<boolean>(true);
  const [showDoiCount, setShowDoiCount] = useState<boolean>(true);

  // The actual aggregation to use is either manually selected or the suggested one
  const aggregation = selectedAggregation || suggestedAggregation;

  // Fetch data based on the current aggregation type
  const fetchChartData = async () => {
    if (!filterState.startDate || !filterState.endDate) {
      throw new Error('Start and end dates are required');
    }

    setLoading(true);

    const options = {
      originHost,
      startDate: filterState.startDate.toISOString().split('T')[0],
      endDate: filterState.endDate.toISOString().split('T')[0],
      aggregation: aggregation,
    };

    try {
      let responseData: Array<any> = [];

      if (emailDomain) {
        const endpoint = '/api/doi-evaluation/rates-by-email-domain-over-time';
        try {
          responseData = await fetchData<any>(endpoint, { ...options, emailDomain }, true);
        } catch (err) {
          console.error('Error fetching email domain DOI rates:', err);
          responseData = [];
        }
      } else {
        const endpoint = '/api/doi-evaluation/rates-over-time';
        try {
          responseData = await fetchData<any>(endpoint, options, true);
        } catch (err) {
          console.error('Error fetching DOI rates:', err);
          responseData = [];
        }
      }

      if (!Array.isArray(responseData)) {
        responseData = [];
      }

      // Find max values for normalization
      const maxSoiCount = Math.max(
        ...responseData.map((item) => item.total_distinct_emails || 0),
        1,
      );
      const maxDoiCount = Math.max(
        ...responseData.map((item) => item.confirmed_distinct_emails || 0),
        1,
      );
      const maxCount = Math.max(maxSoiCount, maxDoiCount);

      // Create 3 series: DOI Rate (%), SOI Count, DOI Count
      const doiRateData: { x: string; y: number }[] = [];
      const soiCountData: { x: string; y: number; originalValue: number }[] = [];
      const doiCountData: { x: string; y: number; originalValue: number }[] = [];

      responseData.forEach((item) => {
        const displayDate = formatDisplayDate(item.date, aggregation);

        // DOI Rate as percentage (already 0-1, multiply by 100)
        doiRateData.push({
          x: displayDate,
          y: item.doi_rate ? parseFloat(String(item.doi_rate)) * 100 : 0,
        });

        // SOI Count - normalize to 0-100 scale, store original value
        const soiOriginal = item.total_distinct_emails || 0;
        soiCountData.push({
          x: displayDate,
          y: (soiOriginal / maxCount) * 100,
          originalValue: soiOriginal,
        });

        // DOI Count - normalize to 0-100 scale, store original value
        const doiOriginal = item.confirmed_distinct_emails || 0;
        doiCountData.push({
          x: displayDate,
          y: (doiOriginal / maxCount) * 100,
          originalValue: doiOriginal,
        });
      });

      const processedData: ISerie[] = [
        {
          id: 'DOI-Rate (%)',
          data: doiRateData,
        },
        {
          id: 'SOI-Anzahl',
          data: soiCountData,
        },
        {
          id: 'DOI-Anzahl',
          data: doiCountData,
        },
      ];

      setChartData(processedData);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching and processing DOI rate data: ', error);
      setLoading(false);
    }
  };

  // Update suggested aggregation when date range changes
  useEffect(() => {
    if (filterState.startDate && filterState.endDate) {
      const newSuggestedAggregation = determineAggregation(
        filterState.startDate,
        filterState.endDate,
      );
      setSuggestedAggregation(newSuggestedAggregation);

      // Reset selected aggregation when date range changes significantly
      if (newSuggestedAggregation !== suggestedAggregation) {
        setSelectedAggregation(null);
      }
    }
  }, [filterState.startDate, filterState.endDate]);

  // Fetch data when aggregation or filters change
  useEffect(() => {
    fetchChartData();
  }, [filterState, originHost, emailDomain, aggregation]);

  // Handle manual aggregation selection
  const handleAggregationChange = (
    event: React.MouseEvent<HTMLElement>,
    newAggregation: AggregationType | null,
  ) => {
    if (newAggregation !== null) {
      setSelectedAggregation(newAggregation);
    }
  };

  // Filter chart data based on visibility state
  const visibleChartData = chartData.filter((series) => {
    if (series.id === 'DOI-Rate (%)') return showDoiRate;
    if (series.id === 'SOI-Anzahl') return showSoiCount;
    if (series.id === 'DOI-Anzahl') return showDoiCount;
    return true;
  });

  if (loading) {
    return <p>Loading chart data...</p>;
  }

  if (chartData.length === 0 || chartData[0].data.length === 0) {
    return <p>No data available for the selected time period.</p>;
  }

  const getAggregationLabel = (agg: AggregationType): string => {
    switch (agg) {
      case 'monthly':
        return 'Monatlich';
      case 'weekly':
        return 'Wöchentlich';
      case 'daily':
      default:
        return 'Täglich';
    }
  };

  return (
    <Box>
      <Box
        mb={2}
        mt={0}
        display="flex"
        justifyContent="space-between"
        alignItems="flex-start"
        flexWrap="wrap"
        gap={2}
      >
        <Box display="flex" flexDirection="column" gap={1}>
          <ToggleButtonGroup
            value={selectedAggregation || suggestedAggregation}
            exclusive
            onChange={handleAggregationChange}
            aria-label="Aggregation Type"
            size="small"
          >
            <ToggleButton value="daily" aria-label="Daily">
              Täglich
            </ToggleButton>
            <ToggleButton value="weekly" aria-label="Weekly">
              Wöchentlich
            </ToggleButton>
            <ToggleButton value="monthly" aria-label="Monthly">
              Monatlich
            </ToggleButton>
          </ToggleButtonGroup>

          {!selectedAggregation && (
            <Typography
              variant="caption"
              sx={{
                color: colors.grey[400],
                fontSize: '0.75rem',
                fontStyle: 'italic',
              }}
            >
              Automatisch gewählt: {getAggregationLabel(suggestedAggregation)}
            </Typography>
          )}
        </Box>

        <FormGroup row>
          <FormControlLabel
            control={
              <Checkbox
                checked={showDoiRate}
                onChange={(e) => setShowDoiRate(e.target.checked)}
                sx={{ color: colors.greenAccent[500] }}
              />
            }
            label="DOI-Rate (%)"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={showSoiCount}
                onChange={(e) => setShowSoiCount(e.target.checked)}
                sx={{ color: colors.blueAccent[500] }}
              />
            }
            label="SOI-Anzahl"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={showDoiCount}
                onChange={(e) => setShowDoiCount(e.target.checked)}
                sx={{ color: colors.redAccent[500] }}
              />
            }
            label="DOI-Anzahl"
          />
        </FormGroup>
      </Box>

      <Box height="400px">
        <ResponsiveLine
          data={visibleChartData}
          margin={{ top: 50, right: 120, bottom: 50, left: 60 }}
          xScale={{ type: 'point' }}
          yScale={{
            type: 'linear',
            min: 0,
            max: 100,
            stacked: false,
          }}
          axisTop={null}
          axisRight={{
            tickSize: 5,
            tickPadding: 5,
            tickRotation: 0,
            legend: 'Anzahl',
            legendOffset: 50,
            legendPosition: 'middle',
            format: (value) => {
              // Find max count from data for right axis scaling
              const maxSoi = Math.max(
                ...(chartData
                  .find((s) => s.id === 'SOI-Anzahl')
                  ?.data.map((d) => d.originalValue || 0) || [1]),
              );
              const maxDoi = Math.max(
                ...(chartData
                  .find((s) => s.id === 'DOI-Anzahl')
                  ?.data.map((d) => d.originalValue || 0) || [1]),
              );
              const maxCount = Math.max(maxSoi, maxDoi);
              return Math.round((value / 100) * maxCount).toString();
            },
          }}
          axisBottom={{
            tickSize: 5,
            tickPadding: 5,
            tickRotation: -45,
            legend: 'Datum',
            legendPosition: 'middle',
            legendOffset: 40,
          }}
          axisLeft={{
            tickSize: 5,
            tickPadding: 5,
            tickRotation: 0,
            legend: 'DOI-Rate (%)',
            legendOffset: -50,
            legendPosition: 'middle',
            format: (value) => `${value.toFixed(0)}%`,
          }}
          enableGridX={false}
          colors={[colors.greenAccent[500], colors.blueAccent[500], colors.redAccent[500]]}
          pointSize={8}
          pointColor={{ theme: 'background' }}
          pointBorderWidth={2}
          pointBorderColor={{ from: 'serieColor' }}
          pointLabelYOffset={-12}
          useMesh={true}
          theme={chartTheme}
          lineWidth={3}
          enableArea={false}
          curve="monotoneX"
          tooltip={({ point }) => {
            const dateText = `Datum: ${point.data.x}`;
            const isCount = point.serieId === 'SOI-Anzahl' || point.serieId === 'DOI-Anzahl';
            const pointData = point.data as any;

            let valueText: string;
            if (isCount && pointData.originalValue !== undefined) {
              // Show original count value with German number formatting
              valueText = `${point.serieId}: ${pointData.originalValue.toLocaleString('de-DE')}`;
            } else {
              // Show percentage
              valueText = `${point.serieId}: ${parseFloat(point.data.yFormatted as string).toFixed(2)}%`;
            }

            return (
              <div
                style={{
                  padding: '8px 12px',
                  color: '#222',
                  background: '#ffffff',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                  borderRadius: '4px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '4px',
                  fontSize: '13px',
                }}
              >
                <p style={{ margin: 0, fontWeight: 'bold' }}>{dateText}</p>
                <p style={{ margin: 0, color: point.serieColor }}>
                  <strong>{valueText}</strong>
                </p>
              </div>
            );
          }}
          legends={[
            {
              anchor: 'bottom-right',
              direction: 'column',
              justify: false,
              translateX: 110,
              translateY: 0,
              itemsSpacing: 2,
              itemDirection: 'left-to-right',
              itemWidth: 100,
              itemHeight: 20,
              itemOpacity: 0.85,
              symbolSize: 12,
              symbolShape: 'circle',
              symbolBorderColor: 'rgba(0, 0, 0, .5)',
              effects: [
                {
                  on: 'hover',
                  style: {
                    itemBackground: 'rgba(0, 0, 0, .03)',
                    itemOpacity: 1,
                  },
                },
              ],
            },
          ]}
        />
      </Box>
    </Box>
  );
};

export default DoiRateOverTimeChart;
